import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TextStyleSize } from '@sirius/typography';

import { ImageDataDb } from '../_shared/data/image-data-db.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import { IButton } from '../_shared/models/button/button.model';
import { IFeature } from '../_shared/models/feature.model';
import { IMediaItem } from '../_shared/models/media/media-item.model';
import { CastPipe } from '../_shared/pipes/cast.pipe';
import { ButtonType } from '../_shared/types/button/button.type';
import { FeatureType } from '../_shared/types/feature.type';
import { withConfigFallback } from '../_shared/utils/with-config-fallback';
import { ButtonComponent } from '../button/button.component';
import { ContainerComponent } from '../container/container.component';
import { HeadingComponent } from '../heading/heading.component';
import { ImageItemComponent } from '../media/media-item/image-item/image-item.component';
import { VideoItemComponent } from '../media/media-item/video-item/video-item.component';
import { TextHtmlComponent } from '../text-html/text-html.component';
import { parseFeatureLayout } from './feature.layout';

const DEFAULT_MEDIA_ITEM: IMediaItem = {
    type: 'image',
    title: '',
};

function featureButtonType(index: number): ButtonType {
    return index === 0 ? 'primary' : 'secondary';
}

@Component({
    selector: 'sirius-feature',
    templateUrl: './feature.component.html',
    styleUrls: ['./feature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        HeadingComponent,
        ImageItemComponent,
        VideoItemComponent,
        TextHtmlComponent,
        LinkDirective,
        ButtonComponent,
        CastPipe,
        ContainerComponent,
    ],
    host: {
        class: 'sir-text-style-paragraph-m-default',
        '[class.is-small]': 'size() === "small"',
        '[class.is-medium]': 'size() === "medium"',
        '[class.is-large]': 'size() === "large"',
        '[class.is-left]': 'align() === "left"',
        '[class.is-right]': 'align() === "right"',
    },
})
export class FeatureComponent implements IDynamicElement<IFeature> {
    readonly config = input<IFeature | undefined>(undefined);
    readonly layout = input<FeatureType>('mediumLeft');
    readonly title = input('');
    readonly textHtml = input('');
    readonly button = input<IButton | IButton[] | undefined>(undefined);
    readonly mediaItem = input<IMediaItem>(DEFAULT_MEDIA_ITEM);

    protected readonly resolvedLayout = withConfigFallback<IFeature, 'layout', FeatureType>(
        this.config,
        'layout',
        this.layout,
    );
    protected readonly resolvedTitle = withConfigFallback<IFeature, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedTextHtml = withConfigFallback<IFeature, 'textHtml', string>(
        this.config,
        'textHtml',
        this.textHtml,
    );
    protected readonly resolvedButton = withConfigFallback<IFeature, 'button'>(this.config, 'button', this.button);
    protected readonly resolvedButtons = computed(() => {
        const button = this.resolvedButton();
        if (!button) {
            return [];
        }

        const items = Array.isArray(button) ? button : [button];

        return items.map((item, index) => ({
            button: item,
            buttonType: featureButtonType(index),
        }));
    });
    protected readonly resolvedMediaItem = withConfigFallback<IFeature, 'media', IMediaItem>(
        this.config,
        'media',
        this.mediaItem,
    );

    private readonly parsedLayout = computed(() => parseFeatureLayout(this.resolvedLayout()));

    /** Feature size variant. Large has no left/right alignment. */
    protected readonly size = computed(() => this.parsedLayout().size);

    /** Media alignment for small and medium layouts only. */
    protected readonly align = computed(() => this.parsedLayout().align);

    protected readonly headingTextStyleSize = computed<TextStyleSize>(() => (this.size() === 'small' ? 'm' : 'l'));

    protected readonly imageData = computed(() => ImageDataDb.feature[this.size()]);

    protected readonly imageAlt = computed(() => {
        const mediaTitle = this.resolvedMediaItem().title?.trim();
        return mediaTitle || this.resolvedTitle();
    });
}
